import { AxiosResponse } from 'axios';
import { APIConfig, throttle } from 'src/config';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import {
  EwasteProduct,
  EWasteRequestDetails,
  UpdateEWasteProduct,
  Document,
  UpdateEwasteStatus,
  RescheduleEwasteRequest,
  EwasteRequest,
} from 'src/interfaces/repair-request/e-waste-request';
import { AssigningModel } from 'src/interfaces/repair-request/repair-request';
import { wait } from 'src/utils/wait';
import axiosClient from '../utils/axios-client';

export class EWasteRequestApi {
  private requestManagementUrl = APIConfig.requestManagementUrl;

  // calls POST api to raise e-waste request in the database
  async raiseEWasteRequest(
    requestDetails: EwasteRequest
  ): Promise<{ responseData: any; statusCode: number }> {
    try {
      const res = await axiosClient
        .post('pickUpRequests', requestDetails, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  // get e-waste request details by request Id
  async getEWasteRequestById(
    requestId: string
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .get(`pickUpRequests/${requestId}`, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  // add e-waste product details
  async addEWasteProduct(
    requestId: string,
    products: EwasteProduct[]
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .put(`pickUpRequests/${requestId}/products`, products, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  // update e-waste product details
  async updateEWasteProduct(
    requestId: string,
    products: UpdateEWasteProduct[]
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .put(`pickUpRequests/${requestId}/product/update`, products, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  // delete e-waste product details
  async deleteEWasteProduct(
    requestId: string,
    productObjectId: string
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .delete(`pickUpRequests/${requestId}/product/${productObjectId}`, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  // update status of request
  async updateStatus(
    requestId: string,
    updateStatusDetails: UpdateEwasteStatus
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .put(`pickUpRequests/${requestId}/status`, updateStatusDetails, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  async addDocument(
    requestId: string,
    documentData: Document
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .put(`pickUpRequests/${requestId}/document`, documentData, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status,
      });
    }
  }

  async deleteDocument(
    requestId: string,
    documentId: string
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .delete(`pickUpRequests/${requestId}/document/${documentId}`, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status,
      });
    }
  }

  async rescheduleRequest(
    options: RescheduleEwasteRequest
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .patch('pickUpRequests/reschedule', options, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  // calls POST api to assignChannelPartner in the database
  async assignEWasteChannelPartner(
    assignChannelPartner: AssigningModel
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .post('pickUpRequests/assign/channelPartner', assignChannelPartner, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status,
      });
    }
  }

  // calls POST api to assign ewaste Technician in the database
  async assignEWasteTechnician(
    assignTechnician: AssigningModel
  ): Promise<{ responseData: EWasteRequestDetails; statusCode: number }> {
    try {
      const res = await axiosClient
        .post('pickUpRequests/assign/technician', assignTechnician, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status,
      });
    }
  }

  async getEWasteRequestsByPaginationAndFilters(
    options: PaginationAndFilter
  ): Promise<{ responseData: any; statusCode: number }> {
    try {
      const res = await axiosClient
        .post('pickUpRequests/getByPaginationAndFilter', options, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  async getEWasteRequestsFilterData(serviceId: string): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }
    const response = await axiosClient.get('pickUpRequests/filters/data', {
      baseURL: this.requestManagementUrl,
      params: {
        serviceId,
      },
    });
    return response;
  }

  async getAllServicesRequestData():Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get(`${this.requestManagementUrl}pickUpRequests/filters/data`);

    return response;
  }

  async generateEWasteReport(
    options: PaginationAndFilter
  ): Promise<{ responseData: any; statusCode: number }> {
    try {
      const res = await axiosClient
        .post('pickUpRequests/download/report/export', options, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }

  async generateEWasteReportExport(
    options: PaginationAndFilter
  ): Promise<{ responseData: any; statusCode: number }> {
    try {
      const res = await axiosClient
        .post('pickUpRequests/download/report/export', options, {
          baseURL: this.requestManagementUrl,
        })
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status,
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status,
      });
    }
  }
}

export const eWasteRequestApi = new EWasteRequestApi();
